'use client';

import React, { useState } from 'react';
import { Plus, Trash2, Activity, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const GOLD = '#faaf40';

export default function AdvancedCalculator() {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate Totals
  const totalCalories = items.reduce((acc, item) => acc + item.calories, 0);
  const totalProtein = items.reduce((acc, item) => acc + item.protein_g, 0);
  const totalCarbs = items.reduce((acc, item) => acc + item.carbohydrates_total_g, 0);
  const totalFat = items.reduce((acc, item) => acc + item.fat_total_g, 0);

  const handleSearch = async () => {
    if (!query) return;
    setLoading(true);
    setError('');

    try {
      // --- THE FIX IS HERE ---
      // We use encodeURIComponent so "Rice & Chicken" doesn't break the URL
      const res = await fetch(`/api/nutrition?query=${encodeURIComponent(query)}`);
      const data = await res.json();

      if (data.items && data.items.length > 0) {
        // Add new items to our list with unique IDs
        const newFoods = data.items.map(food => ({
            ...food,
            id: Date.now() + Math.random() // Unique ID
        }));
        
        setItems([...items, ...newFoods]);
        setQuery('');
      } else {
        setError("Could not find food. Try something simpler like '1 cup rice'.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please check your internet.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  return (
    <section className="py-20 bg-black text-white flex justify-center px-4">
      <div className="w-full max-w-3xl bg-[#0a0a0a] border border-[#faaf40]/30 rounded-2xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
        
        {/* Glow Effect */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#faaf40] blur-[120px] opacity-10 pointer-events-none" />

        {/* Header */}
        <div className="text-center mb-8 relative z-10">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-[#faaf40]/10 mb-4">
            <Zap color={GOLD} size={32} />
          </div>
          <h2 className="text-3xl font-extrabold uppercase italic text-[#faaf40]">
            AI Nutrition Tracker
          </h2>
          <p className="text-gray-400 text-sm mt-2">
            Type anything like: <span className="text-white font-semibold">"2 eggs and 1 cup rice"</span>
          </p>
        </div>

        {/* Input Area */}
        <div className="flex gap-2 mb-6 relative z-10">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="e.g. 300g chicken breast and salad"
            className="flex-1 bg-[#1a1a1a] border border-white/20 rounded-lg px-4 py-4 text-white focus:border-[#faaf40] focus:outline-none transition-all placeholder-gray-600"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="bg-[#faaf40] text-black font-bold px-6 rounded-lg hover:bg-white transition-all disabled:opacity-50 min-w-[60px] flex items-center justify-center"
          >
            {loading ? <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div> : <Plus size={24} />}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-400 text-center text-sm mb-4 bg-red-900/20 p-2 rounded">
            {error}
          </div>
        )}

        {/* Food List */}
        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar relative z-10 mb-8">
          <AnimatePresence>
            {items.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center justify-between bg-[#111] border border-white/10 p-4 rounded-xl group hover:border-[#faaf40]/40 transition-colors"
              >
                <div>
                  <h4 className="text-lg font-bold capitalize text-white group-hover:text-[#faaf40] transition-colors">
                    {item.name} <span className="text-xs text-gray-500 font-normal">({item.serving_size_g}g)</span>
                  </h4>
                  {/* Detailed Macros */}
                  <div className="text-xs text-gray-400 flex gap-3 mt-1">
                    <span className="text-blue-400">P: {item.protein_g}g</span>
                    <span className="text-green-400">C: {item.carbohydrates_total_g}g</span>
                    <span className="text-yellow-400">F: {item.fat_total_g}g</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-[#faaf40] font-bold text-xl">{Math.round(item.calories)}</span>
                  <button 
                    onClick={() => handleRemove(item.id)}
                    className="text-gray-600 hover:text-red-500 transition-colors p-2"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {items.length === 0 && !loading && (
            <div className="text-center text-gray-700 py-8">
              Start adding your meals to calculate nutrition.
            </div>
          )}
        </div>

        {/* Total Footer */}
        <div className="border-t border-white/10 pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
                <span className="text-gray-400 uppercase tracking-widest text-sm font-bold">Total Daily Intake</span>
                <span className="text-5xl font-extrabold text-white" style={{ textShadow: `0 0 25px ${GOLD}50` }}>
                    {Math.round(totalCalories)} <span className="text-lg font-medium text-[#faaf40]">kcal</span>
                </span>
            </div>
            
            {/* Macro Bars */}
            <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-[#1a1a1a] p-3 rounded-lg border border-blue-500/20">
                    <div className="text-xs text-blue-400 font-bold mb-1">PROTEIN</div>
                    <div className="text-xl font-bold text-white">{Math.round(totalProtein)}g</div>
                </div>
                <div className="bg-[#1a1a1a] p-3 rounded-lg border border-green-500/20">
                    <div className="text-xs text-green-400 font-bold mb-1">CARBS</div>
                    <div className="text-xl font-bold text-white">{Math.round(totalCarbs)}g</div>
                </div>
                <div className="bg-[#1a1a1a] p-3 rounded-lg border border-yellow-500/20">
                    <div className="text-xs text-yellow-400 font-bold mb-1">FATS</div>
                    <div className="text-xl font-bold text-white">{Math.round(totalFat)}g</div>
                </div>
            </div>
        </div>

      </div>
    </section>
  );
}